/**
 * @class PdpCartUpsell
 * Shows a recommended upsell popup after PDP add to cart, before the cart drawer opens.
 * Supports up to two CMS products (desktop) with mobile visibility controls.
 * Decline suppression uses first-party browser storage only (GDPR-friendly).
 */
const PDP_CART_UPSELL_STORAGE_VERSION = 1;

function pdpCartUpsellStorageKey(config) {
  return config.storageKey || 'gp_cart_upsell_dismiss_v1';
}

function pdpCartUpsellReadRecord(config) {
  const key = pdpCartUpsellStorageKey(config);
  const raw =
    config.storage === 'session'
      ? sessionStorage.getItem(key)
      : localStorage.getItem(key);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function pdpCartUpsellWriteRecord(config, record) {
  const key = pdpCartUpsellStorageKey(config);
  const payload = JSON.stringify(record);

  if (config.storage === 'session') {
    sessionStorage.setItem(key, payload);
  } else {
    localStorage.setItem(key, payload);
  }
}

function pdpCartUpsellClearRecord(config) {
  const key = pdpCartUpsellStorageKey(config);
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
}

function pdpCartUpsellIsExpired(record) {
  if (!record?.expiresAt) {
    return false;
  }

  return Date.now() > new Date(record.expiresAt).getTime();
}

function pdpCartUpsellIsDesignMode() {
  return window.Shopify && window.Shopify.designMode === true;
}

if (!customElements.get('pdp-cart-upsell')) {
  class PdpCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.lastAddedProductId = null;
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-upsell-dialog]');
      this.productsContainer = this.querySelector('[data-upsell-products]');
      this.addLabel = this.dataset.addLabel || 'Add to cart';
      this.chooseOptionsLabel = this.dataset.chooseOptionsLabel || 'Choose options';

      this.querySelectorAll('[data-upsell-dismiss]').forEach((button) => {
        button.addEventListener('click', () => this.dismiss());
      });

      this.querySelector('[data-upsell-overlay]')?.addEventListener('click', () => this.dismiss());

      this.productsContainer?.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-upsell-add]');
        if (addButton) {
          event.preventDefault();
          const product = addButton.closest('.pdp-cart-upsell__product');
          this.addUpsellToCart(product, addButton);
          return;
        }

        const chooseOptions = event.target.closest('[data-upsell-choose-options]');
        if (chooseOptions) {
          window.pdpCartUpsellBypass = true;
          this.hideUpsellOnly();
        }
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.dismiss();
        }
      };

      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    getConfig() {
      const script = document.getElementById(`PdpCartUpsellConfig-${this.dataset.sectionId}`);

      if (!script) {
        return null;
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    isDismissSuppressed(productId) {
      const config = this.getConfig();

      if (!config?.suppressionEnabled || pdpCartUpsellIsDesignMode()) {
        return false;
      }

      const record = pdpCartUpsellReadRecord(config);

      if (!record || record.v !== PDP_CART_UPSELL_STORAGE_VERSION) {
        return false;
      }

      if (String(record.shopId) !== String(config.shopId)) {
        return false;
      }

      if (pdpCartUpsellIsExpired(record)) {
        pdpCartUpsellClearRecord(config);
        return false;
      }

      if (config.scope === 'per_product') {
        const ids = (record.productIds || []).map(String);
        return ids.includes(String(productId));
      }

      return true;
    }

    saveDismissRecord(productId) {
      const config = this.getConfig();

      if (!config?.suppressionEnabled || pdpCartUpsellIsDesignMode()) {
        return;
      }

      const now = new Date();
      const record = {
        v: PDP_CART_UPSELL_STORAGE_VERSION,
        shopId: config.shopId,
        scope: config.scope,
        dismissedAt: now.toISOString(),
      };

      if (config.storage === 'local' && config.retentionDays > 0) {
        const expires = new Date(now);
        expires.setDate(expires.getDate() + config.retentionDays);
        record.expiresAt = expires.toISOString();
      }

      if (config.scope === 'per_product' && productId) {
        const existing = pdpCartUpsellReadRecord(config);
        const ids = new Set(
          (existing && existing.productIds ? existing.productIds : []).map(String)
        );
        ids.add(String(productId));
        record.productIds = Array.from(ids);
      }

      pdpCartUpsellWriteRecord(config, record);
    }

    getProductNodes() {
      return Array.from(this.productsContainer?.querySelectorAll('.pdp-cart-upsell__product') || []);
    }

    async present(cartResponse) {
      if (this.dataset.enabled === 'false' || window.pdpCartUpsellBypass) {
        window.pdpCartUpsellBypass = false;
        return false;
      }

      const addedItem = this.getAddedItem(cartResponse);
      this.lastAddedProductId = addedItem?.product_id || null;

      if (this.isDismissSuppressed(this.lastAddedProductId)) {
        return false;
      }

      const hasCmsProducts = this.dataset.hasCmsProducts === 'true';

      if (hasCmsProducts) {
        this.resetProductVisibility();
        const visibleCount = await this.filterVisibleProducts(addedItem);

        if (visibleCount === 0) {
          return false;
        }

        this.show();
        return true;
      }

      const productNodes = await this.resolveFallbackProducts(addedItem);

      if (!productNodes.length) {
        return false;
      }

      this.productsContainer.innerHTML = '';
      productNodes.forEach((node, index) => {
        node.dataset.mobileSlot = String(index + 1);
        this.productsContainer.appendChild(node);
      });

      const visibleCount = await this.filterVisibleProducts(addedItem);

      if (visibleCount === 0) {
        return false;
      }

      this.show();
      return true;
    }

    resetProductVisibility() {
      this.getProductNodes().forEach((product) => {
        product.hidden = false;
        product.removeAttribute('data-skipped');
      });
    }

    async filterVisibleProducts(addedItem) {
      let visibleCount = 0;

      for (const product of this.getProductNodes()) {
        if (await this.shouldSkipProduct(product, addedItem)) {
          product.hidden = true;
          product.setAttribute('data-skipped', 'true');
        } else {
          visibleCount += 1;
        }
      }

      if (this.productsContainer) {
        this.productsContainer.dataset.visibleCount = String(visibleCount);
      }

      return visibleCount;
    }

    getAddedItem(cartResponse) {
      if (!cartResponse) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(cartResponse, 'items') && cartResponse.items.length) {
        return cartResponse.items[0];
      }

      return cartResponse;
    }

    async resolveFallbackProducts(addedItem) {
      const source = this.dataset.source || 'recommendations_then_metafield';
      const productId = addedItem?.product_id;
      const nodes = [];

      if (source !== 'metafield' && productId) {
        // Complementary needs products set in Search & Discovery; fall back to related.
        const recommendationNodes = await this.fetchRecommendationNodes(productId, 'complementary');

        if (recommendationNodes.length) {
          nodes.push(...recommendationNodes);
        } else {
          nodes.push(...(await this.fetchRecommendationNodes(productId, 'related')));
        }
      }

      if (!nodes.length && source !== 'recommendations') {
        const template = this.querySelector('template[data-metafield-fallback]');
        const metafieldNode = template?.content.querySelector('.pdp-cart-upsell__product');

        if (metafieldNode) {
          nodes.push(metafieldNode.cloneNode(true));
        }
      }

      return nodes;
    }

    async fetchRecommendationNodes(productId, intent = 'complementary') {
      const baseUrl = this.dataset.recommendationsUrl || '';
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', String(productId));
      url.searchParams.set('intent', intent);
      url.searchParams.set('limit', '2');

      try {
        const response = await fetch(`${url.pathname}${url.search}`);
        const text = await response.text();
        const html = document.createElement('div');
        html.innerHTML = text;

        return Array.from(html.querySelectorAll('.pdp-cart-upsell__product')).slice(0, 2);
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    async shouldSkipProduct(productNode, addedItem) {
      const upsellProductId = parseInt(productNode.dataset.productId, 10);
      const addedProductId = parseInt(addedItem?.product_id, 10);

      if (
        this.dataset.skipSameProduct !== 'false' &&
        upsellProductId &&
        addedProductId &&
        upsellProductId === addedProductId
      ) {
        return true;
      }

      if (this.dataset.skipInCart === 'false') {
        return false;
      }

      try {
        const response = await fetch(`${window.theme?.routes?.root_url || '/'}cart.js`);
        const cart = await response.json();

        return cart.items.some((item) => parseInt(item.product_id, 10) === upsellProductId);
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    hideUpsellOnly() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-active');
      this.isOpen = false;
      document.body.classList.remove('open-cc-upsell');
    }

    show() {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-active');
      this.isOpen = true;
      document.body.classList.add('open-cc-upsell');
      this.dialog?.focus();
    }

    dismiss() {
      this.saveDismissRecord(this.lastAddedProductId);
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-active');
      this.isOpen = false;
      document.body.classList.remove('open-cc-upsell');
      this.openCartDrawer();
    }

    openCartDrawer() {
      const cartDrawer = document.getElementById('Cart-Drawer');
      const searchDrawer = document.getElementById('Search-Drawer');

      if (searchDrawer?.classList.contains('active')) {
        searchDrawer.classList.remove('active');
      }

      if (cartDrawer) {
        document.body.classList.add('open-cc');
        cartDrawer.classList.add('active');
        cartDrawer.focus();

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart-drawer:open');
        }
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'Cart',
          section: 'main-cart',
          selector: '.thb-cart-form',
        },
        {
          id: 'Cart-Drawer',
          section: 'cart-drawer',
          selector: '.cart-drawer',
        },
        {
          id: 'cart-drawer-toggle',
          section: 'cart-bubble',
          selector: '.thb-item-count',
        },
      ];
    }

    updateCartSections(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const target = document.getElementById(section.id);

        if (!target || !parsedState.sections?.[section.section]) {
          return;
        }

        const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
        const source = html.querySelector(section.selector) || html.querySelector('.shopify-section');

        if (!source) {
          return;
        }

        const destination = target.querySelector(section.selector) || target;
        destination.innerHTML = source.innerHTML;
      });

      if (typeof CartDrawer !== 'undefined') {
        new CartDrawer();
      }

      if (typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }
    }

    async addUpsellToCart(productNode, addButton) {
      if (!productNode || addButton?.classList.contains('loading')) {
        return;
      }

      const variantId = productNode.dataset.variantId;

      if (!variantId) {
        return;
      }

      addButton.classList.add('loading');
      addButton.setAttribute('aria-disabled', 'true');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section).join(','));
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        });

        const parsedState = await response.json();

        if (parsedState.status) {
          console.error(parsedState.description || parsedState.message);
          return;
        }

        this.updateCartSections(parsedState);

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: Object.prototype.hasOwnProperty.call(parsedState, 'items') ? parsedState.items[0] : parsedState,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        addButton.classList.remove('loading');
        addButton.removeAttribute('aria-disabled');
        this.dismissWithoutSuppress();
      }
    }

    dismissWithoutSuppress() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-active');
      this.isOpen = false;
      document.body.classList.remove('open-cc-upsell');
      this.openCartDrawer();
    }
  }

  customElements.define('pdp-cart-upsell', PdpCartUpsell);
}
